import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import qs from 'query-string';

import { Icon, Text } from 'shared/components/ui';
import {
	Header,
	ResultsSidebar,
	TechnographicsCompaniesResultsList,
	TechnographicsResultsList
} from 'features/technographics';
import { useFilters, useGetCandidates, useGetTechnograpicsCompanies } from 'features/search';
import { useGetCompanyCountQuery } from 'services';
import { SearchParams, CompanySearchParams } from 'shared/generated-models';

import styles from './index.module.scss';

type searchType = 'people' | 'companies';

const PEOPLE_FILTERS: (keyof SearchParams)[] = ['locations', 'countries', 'seniority'];
const COMPANY_FILTERS: (keyof CompanySearchParams)[] = [
	'city',
	'country',
	'company_domain',
	'website_keywords',
	'headcount'
];

function formatNumberWithCommas(number?: number) {
	if (!number) return '';
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const TechnographicsResults = () => {
	const [progress, setProgress] = useState<'results' | 'export'>('results');
	const navigate = useNavigate();
	const filters = useFilters() as SearchParams & CompanySearchParams;

	const tags = filters['required_skills'] ?? [];
	const [tab, setTab] = useState<searchType>('companies');

	const handleChangeTab = (type: searchType) => () => {
		setTab(type);
	};

	const peopleFilters = useMemo(() => {
		const tags: string[] = [];

		PEOPLE_FILTERS.forEach(filter => {
			const value = filters[filter];
			if (Array.isArray(value) && value.length > 0) {
				value.forEach(tag => tags.push(tag));
			}
		});
		return tags;
	}, [filters]);

	const companyFilters = useMemo(() => {
		const tags: string[] = [];

		COMPANY_FILTERS.forEach(filter => {
			const value = filters[filter];
			if (Array.isArray(value) && value.length > 0) {
				value.forEach(tag => tags.push(tag));
			}
		});
		return tags;
	}, [filters]);

	const handleChangeFilters = () => {
		const filtersCopy = { ...filters };
		if (filters['skills']) filtersCopy['tags'] = filters['skills'].join(',');
		delete filtersCopy['skills'];
		delete filtersCopy['required_skills'];
		delete filtersCopy['page'];
		delete filtersCopy['size'];
		const cleanParams: Record<string, any> = Object.entries(filtersCopy)
			.filter(([_, v]) => (Array.isArray(v) ? v.length > 0 : v != null))
			.reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

		const search = qs.stringify(
			{ ...cleanParams },
			{
				arrayFormat: 'bracket-separator',
				arrayFormatSeparator: ','
			}
		);
		navigate({ pathname: '/technographics/search', search: search });
	};

	const handleRefineCriteria = () => {
		navigate('/technographics/chat');
	};

	const {
		data: companiesData,
		isFetching: companiesIsFetching,
		isError: companiesIsError
	} = useGetTechnograpicsCompanies();

	const { data, isFetching, isError } = useGetCandidates();

	const { page: filtersPage, size, ...filteredFilters } = filters;

	const { data: profileData, isFetching: isFetchingProfile } = useGetCompanyCountQuery({
		...filteredFilters
	});
	const isPeopleSpecific =
		'locations' in filteredFilters || 'countries' in filteredFilters || 'seniority' in filteredFilters;

	return (
		<>
			<Header progress={progress} onClick={handleChangeFilters} />
			<div className={styles.container}>
				<div className={styles.wrapper}>
					<div className={styles.sidebarWrapper}>
						<ResultsSidebar
							tags={tags!}
							filters
							peopleFilters={peopleFilters}
							companiesFilters={companyFilters}
							handleChangeFilters={handleChangeFilters}
							handleRefineCriteria={handleRefineCriteria}
						/>
					</div>
					<div className={styles.filtersWrapper}>
						<Text variant="inter/28/semi">
							Search results:{' '}
							<span className={styles.regular}>{tab === 'people' ? 'People' : 'Companies'}</span>
						</Text>
						<div className={styles.tabs}>
							<div
								className={cn(styles.tab, { [styles.tabActiveCompanies]: tab === 'companies' })}
								onClick={handleChangeTab('companies')}>
								<Icon
									icon="buildings"
									size={24}
									className={cn(styles.tabIcon, {
										[styles.tabIconActiveCompanies]: tab === 'companies'
									})}
								/>
								<div className={styles.tabTitle}>
									<Text
										variant="inter/15/bold"
										color={tab === 'companies' ? 'green_new_dark' : 'grey'}>
										{formatNumberWithCommas(companiesData.results_count)}
									</Text>
									<Text
										variant="inter/15/regular"
										color={tab === 'companies' ? 'green_new_dark' : 'grey'}>
										Companies
									</Text>
								</div>
							</div>
							<div
								className={cn(styles.tab, { [styles.tabActivePeople]: tab === 'people' })}
								onClick={handleChangeTab('people')}>
								<Icon
									icon="users"
									size={24}
									className={cn(styles.tabIcon, { [styles.tabIconActivePeople]: tab === 'people' })}
								/>
								<div className={styles.tabTitle}>
									<Text variant="inter/15/bold" color={tab === 'people' ? 'blue_new_dark' : 'grey'}>
										{formatNumberWithCommas(
											isPeopleSpecific ? data.results_count : profileData?.results_count
										)}
									</Text>
									<Text
										variant="inter/15/regular"
										color={tab === 'people' ? 'blue_new_dark' : 'grey'}>
										People
									</Text>
								</div>
							</div>
						</div>
						<div className={styles.filtersContainer}>
							{tab === 'people' ? (
								<TechnographicsResultsList
									data={data}
									isFetching={isFetching || isFetchingProfile}
									isError={isError}
									setProgress={setProgress}
									total={isPeopleSpecific ? data.results_count : profileData?.results_count}
								/>
							) : (
								<TechnographicsCompaniesResultsList
									data={companiesData}
									isFetching={companiesIsFetching}
									isError={companiesIsError}
									setProgress={setProgress}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
